import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

export default function Sellers() {
  const { user } = useAuth();
  const canView = ['super_admin', 'broker', 'agent'].includes(user?.role);

  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await authFetch(`${API}/api/sellers`);
      if (res.ok) setSellers(await res.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, []); // eslint-disable-line

  const openSeller = async (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id); setDetail(null);
    const res = await authFetch(`${API}/api/sellers/${id}`);
    if (res.ok) setDetail(await res.json());
  };

  const addSeller = async (e) => {
    e.preventDefault(); setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    const res = await authFetch(`${API}/api/sellers`, { method: 'POST', body: JSON.stringify(form) });
    if (res.ok) { setForm({ name: '', email: '', phone: '', notes: '' }); setShowAdd(false); load(); }
    else { const er = await res.json(); setError(er.detail || 'Failed to add seller'); }
  };

  if (!canView) {
    return (
      <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12"><div className="max-w-4xl mx-auto px-4">
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
          <h1 className="text-white text-2xl font-bold mb-2">Sellers</h1>
          <p className="text-[#B0BEC5]">Available to agents and brokers only.</p>
        </div></div></div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6 pb-12">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Sellers</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">Seller contacts you own, linked to their listings and (if they're also buying) their buyer profile.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/import" className="px-3 py-2 bg-[#243B55] text-[#00D4FF] font-medium rounded-lg hover:bg-[#2d4a6a] text-sm">Import</Link>
            <button onClick={() => setShowAdd(!showAdd)} className="px-3 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">+ Seller</button>
          </div>
        </div>

        {error && <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-2.5 rounded-lg mb-3 text-sm">{error}</div>}

        {showAdd && (
          <form onSubmit={addSeller} className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm" />
            <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm" />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm" />
            <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm" />
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">Add seller</button>
              <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-2 text-gray-400 hover:text-white text-sm">Cancel</button>
            </div>
          </form>
        )}

        {loading ? <div className="text-[#00D4FF] py-16 text-center">Loading…</div>
          : sellers.length === 0 ? (
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-14 px-6">
              <p className="text-[#B0BEC5] mb-2">No sellers yet</p>
              <p className="text-gray-500 text-sm">Add one above, or import a CSV with seller rows.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sellers.map(s => (
                <div key={s.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
                  <button onClick={() => openSeller(s.id)} className="w-full text-left p-4 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{s.name}
                        {s.buyer_id && <span className="ml-2 text-[10px] text-[#00E676] border border-[#00E676]/40 rounded px-1">also a buyer</span>}
                      </div>
                      <div className="text-gray-400 text-xs truncate">{[s.email, s.phone].filter(Boolean).join(' · ')}</div>
                    </div>
                    <div className="text-gray-400 text-xs shrink-0">{s.property_count} listing{s.property_count === 1 ? '' : 's'} ▾</div>
                  </button>
                  {openId === s.id && (
                    <div className="px-4 pb-4 border-t border-[#546E7A]/20 pt-3">
                      {!detail ? <div className="text-gray-500 text-sm">Loading…</div> : (
                        <div className="space-y-3 text-sm">
                          {detail.notes && <div className="text-gray-300">{detail.notes}</div>}
                          {/* dual-role cross-link to buyer match board */}
                          {detail.buyer_id && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">Also buying:</span>
                              <Link to={`/match-board?buyer=${detail.buyer_id}`} className="text-[#00D4FF] hover:text-[#00E676]">
                                {detail.buyer_name || 'View buyer match board'} →
                              </Link>
                            </div>
                          )}
                          {/* linked listings / drafts */}
                          <div>
                            <div className="text-gray-400 mb-1">Listings ({detail.properties.length})</div>
                            {detail.properties.length === 0 ? <div className="text-gray-500 text-xs">No linked listings.</div> : (
                              <div className="space-y-1">
                                {detail.properties.map(p => (
                                  <div key={p.id} className="flex items-center justify-between gap-2">
                                    <span className="text-white truncate">{p.address || `Property #${p.id}`}
                                      {p.is_draft && <span className="ml-2 text-[10px] text-[#00D4FF] border border-[#00D4FF]/40 rounded px-1">draft</span>}
                                    </span>
                                    <Link to={p.is_draft ? `/listing-lab/${p.id}` : `/properties/${p.id}`} className="text-[#00D4FF] hover:text-[#00E676] text-xs shrink-0">
                                      {p.is_draft ? 'Open Listing Lab →' : 'Open →'}
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
